import React, { useState } from "react";
import {
  Form,
  Space,
  Checkbox,
  Button,
  Switch,
  DatePicker,
  Divider,
  Input,
} from "antd";
import { translate } from "../../../../core";
import { Combo } from "../../../../core/component";
import { ItemCategory } from "../../../inventory/type";
import { dateFormat } from "../../../../core/until";
import { MassChangeFormProps } from "./types";
import styles from "../MassChange.module.scss";
import { PlusOutlined } from "@ant-design/icons";
import { useItemCategoryData } from "../../../inventory/hook/item-category-data-hook";

const MassChangeForm: React.FC<MassChangeFormProps> = ({
  form,
  enabledFields,
  setEnabledFields,
  categoryId,
  setCategoryId,
  subcategoryId,
  setSubcategoryId,
  setSubcategory2Id,
  categoryOptions,
  subcategoryOptions,
  subcategory2Options,
  isFormValid,
  massChangeLoading,
  handleFormSubmit,
  refreshCategories,
}) => {
  const { create } = useItemCategoryData();

  // Adding states for new category inputs
  const [newCategoryTitle, setNewCategoryTitle] = useState<string>();
  const [newSubcategoryTitle, setNewSubcategoryTitle] = useState<string>();
  const [newSubcategory2Title, setNewSubcategory2Title] = useState<string>();
  const [savingCategory, setSavingCategory] = useState<boolean>(false);
  const [savingSubcategory, setSavingSubcategory] = useState<boolean>(false);
  const [savingSubcategory2, setSavingSubcategory2] = useState<boolean>(false);

  const handleCategoryCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setEnabledFields({
      ...enabledFields,
      categoryId: checked,
      subcategoryId: checked ? enabledFields.subcategoryId : false,
      subcategory2Id: checked ? enabledFields.subcategory2Id : false,
    });
    if (!checked) {
      setCategoryId(null);
      setSubcategoryId(null);
      setSubcategory2Id(null);
      form.setFields([
        { name: "categoryId", errors: [] },
        { name: "subcategoryId", errors: [] },
        { name: "subcategory2Id", errors: [] },
      ]);
    }
  };

  const handleSubcategoryCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setEnabledFields({
      ...enabledFields,
      subcategoryId: checked,
      subcategory2Id: checked ? enabledFields.subcategory2Id : false,
    });
    if (!checked) {
      setSubcategoryId(null);
      setSubcategory2Id(null);
      form.setFields([
        { name: "subcategoryId", errors: [] },
        { name: "subcategory2Id", errors: [] },
      ]);
    }
  };

  const handleSubcategory2CheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setEnabledFields({
      ...enabledFields,
      subcategory2Id: checked,
    });
    if (!checked) {
      setSubcategory2Id(null);
      form.setFields([{ name: "subcategory2Id", errors: [] }]);
    }
  };

  const handleSaleStartDateCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setEnabledFields({
      ...enabledFields,
      saleStartDate: checked,
    });
    if (!checked) {
      form.setFieldsValue({ saleStartDate: null });
      form.setFields([{ name: "saleStartDate", errors: [] }]);
    }
  };

  const handleSaleEndDateCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setEnabledFields({
      ...enabledFields,
      saleEndDate: checked,
    });
    if (!checked) {
      form.setFieldsValue({ saleEndDate: null });
      form.setFields([{ name: "saleEndDate", errors: [] }]);
    }
  };

  const handleShowOnWebCheckboxChange = (e: any) => {
    setEnabledFields({
      ...enabledFields,
      showOnWeb: e.target.checked,
    });
  };

  const handleNonPurchasableCheckboxChange = (e: any) => {
    setEnabledFields({
      ...enabledFields,
      nonPurchasable: e.target.checked,
    });
  };

  // Handle Combo onChange events
  const handleCategoryChange = async (value: number | number[]) => {
    const categoryValue = Array.isArray(value) ? value[0] : value;
    setCategoryId(categoryValue || null);

    // Clear subcategory and subcategory2 when category changes
    setSubcategoryId(null);
    setSubcategory2Id(null);

    // Reset form values for subcategories
    form.setFieldsValue({
      subcategoryId: undefined,
      subcategory2Id: undefined,
    });

    // Disable subcategory fields if no category is selected
    if (!categoryValue) {
      setEnabledFields({
        ...enabledFields,
        subcategoryId: false,
        subcategory2Id: false,
      });
    }

    // Refresh the category lists to update available options
    await refreshCategories();
  };

  const handleSubcategoryChange = async (value: number | number[]) => {
    const subcategoryValue = Array.isArray(value) ? value[0] : value;
    setSubcategoryId(subcategoryValue || null);

    // Clear subcategory2 when subcategory changes
    setSubcategory2Id(null);

    // Reset form values for subcategory2
    form.setFieldsValue({
      subcategory2Id: undefined,
    });

    // Refresh the category lists to update available options
    await refreshCategories();
  };

  const handleSubcategory2Change = async (value: number | number[]) => {
    const subcategory2Value = Array.isArray(value) ? value[0] : value;
    setSubcategory2Id(subcategory2Value || null);

    // Refresh the category lists to update available options
    await refreshCategories();
  };

  // Category dropdown components
  const CategoryDropdown = ({ menu, onSave, title, setTitle, saving }: any) => {
    // Add ref to maintain focus
    const inputRef = React.useRef<any>(null);

    // Focus input when dropdown opens
    React.useEffect(() => {
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }, []);

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Space
          style={{ padding: "0 8px 4px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={translate("please_enter")}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              // Submit on Enter if title is valid
              if (e.key === "Enter" && title?.length && title.length >= 3) {
                onSave(title);
              }
            }}
            autoFocus
          />
          <Button
            disabled={!(title?.length && title.length >= 3)}
            type="text"
            shape="circle"
            icon={<PlusOutlined />}
            loading={saving}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (!title) return;
              onSave(title);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </Space>
        <Divider style={{ margin: "8px 0" }} />
        {menu}
      </div>
    );
  };

  return (
    <div className={styles.formContainer}>
      <h3 className={styles.formTitle}>
        {translate("mass_changes", "options")}
      </h3>

      {/* Attention Box - only show when category or subcategory is selected */}
      {(enabledFields.categoryId || enabledFields.subcategoryId) && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 4,
            color: "#ff4d4f",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          <strong>**Attention:**</strong> Changing a category will clear
          subcategory 1 & 2. Similarly, changing subcategory 1 will clear
          subcategory 2. Please set all necessary category levels to maintain
          proper hierarchy.
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormSubmit}
        initialValues={{
          categoryId: enabledFields.categoryId,
          subcategoryId: enabledFields.subcategoryId,
          subcategory2Id: enabledFields.subcategory2Id,
          saleStartDate: enabledFields.saleStartDate,
          saleEndDate: enabledFields.saleEndDate,
          showOnWeb: enabledFields.showOnWeb,
          nonPurchasable: enabledFields.nonPurchasable,
          nonPurchasablePriceEnabled: enabledFields.nonPurchasablePriceEnabled,
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Category Selection */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.categoryId}
              onChange={handleCategoryCheckboxChange}
              className={styles.checkbox}
            >
              {translate("category")}
            </Checkbox>
            <Form.Item
              name="categoryId"
              className={styles.formItem}
              rules={[
                {
                  required: enabledFields.categoryId,
                  message: "Required",
                },
              ]}
            >
              <Combo<ItemCategory, number>
                placeholder={translate("category")}
                options={categoryOptions}
                onChange={handleCategoryChange}
                disabled={!enabledFields.categoryId}
                size="middle"
                style={{ width: "100%" }}
                dropdownRender={(menu) => (
                  <CategoryDropdown
                    menu={menu}
                    title={newCategoryTitle}
                    setTitle={setNewCategoryTitle}
                    saving={savingCategory}
                    onSave={async (title: string) => {
                      setSavingCategory(true);
                      try {
                        const { id } = await create({ title });
                        // Update the category field
                        setCategoryId(id);
                        form.setFieldsValue({ categoryId: id });
                        setNewCategoryTitle(undefined);

                        // Refresh all categories from the parent component
                        await refreshCategories();
                      } finally {
                        setSavingCategory(false);
                      }
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Subcategory Selection */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.subcategoryId}
              onChange={handleSubcategoryCheckboxChange}
              className={styles.checkbox}
              disabled={!enabledFields.categoryId}
            >
              {translate("subcategory")}
            </Checkbox>
            <Form.Item
              name="subcategoryId"
              className={styles.formItem}
              rules={[
                {
                  required: enabledFields.subcategoryId,
                  message: "Required",
                },
              ]}
            >
              <Combo<ItemCategory, number>
                placeholder={translate("subcategory")}
                options={subcategoryOptions}
                onChange={handleSubcategoryChange}
                disabled={!enabledFields.subcategoryId || !categoryId}
                size="middle"
                style={{ width: "100%" }}
                dropdownRender={(menu) => (
                  <CategoryDropdown
                    menu={menu}
                    title={newSubcategoryTitle}
                    setTitle={setNewSubcategoryTitle}
                    saving={savingSubcategory}
                    onSave={async (title: string) => {
                      setSavingSubcategory(true);
                      try {
                        const { id } = await create({
                          title,
                          parentId: categoryId,
                        });
                        // Update the subcategory field
                        setSubcategoryId(id);
                        form.setFieldsValue({ subcategoryId: id });
                        setNewSubcategoryTitle(undefined);

                        // Refresh all categories from the parent component
                        await refreshCategories();
                      } finally {
                        setSavingSubcategory(false);
                      }
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Subcategory2 Selection */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.subcategory2Id}
              onChange={handleSubcategory2CheckboxChange}
              className={styles.checkbox}
              disabled={!enabledFields.subcategoryId}
            >
              {translate("subcategory2")}
            </Checkbox>
            <Form.Item
              name="subcategory2Id"
              className={styles.formItem}
              rules={[
                {
                  required: enabledFields.subcategory2Id,
                  message: "Required",
                },
              ]}
            >
              <Combo<ItemCategory, number>
                placeholder={translate("subcategory2")}
                options={subcategory2Options}
                onChange={handleSubcategory2Change}
                disabled={!enabledFields.subcategory2Id || !subcategoryId}
                size="middle"
                style={{ width: "100%" }}
                dropdownRender={(menu) => (
                  <CategoryDropdown
                    menu={menu}
                    title={newSubcategory2Title}
                    setTitle={setNewSubcategory2Title}
                    saving={savingSubcategory2}
                    onSave={async (title: string) => {
                      setSavingSubcategory2(true);
                      try {
                        const { id } = await create({
                          title,
                          parentId: subcategoryId,
                        });
                        // Update the subcategory2 field
                        setSubcategory2Id(id);
                        form.setFieldsValue({ subcategory2Id: id });
                        setNewSubcategory2Title(undefined);

                        // Refresh all categories from the parent component
                        await refreshCategories();
                      } finally {
                        setSavingSubcategory2(false);
                      }
                    }}
                  />
                )}
              />
            </Form.Item>
          </div>

          {/* Sale Start Date */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.saleStartDate}
              onChange={handleSaleStartDateCheckboxChange}
              className={styles.checkbox}
            >
              {translate("sale", "start", "date")}
            </Checkbox>
            <Form.Item
              name="saleStartDate"
              className={styles.formItem}
              rules={[
                {
                  required: enabledFields.saleStartDate,
                  message: "Required",
                },
              ]}
            >
              <DatePicker
                format={dateFormat}
                disabled={!enabledFields.saleStartDate}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          {/* Sale End Date */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.saleEndDate}
              onChange={handleSaleEndDateCheckboxChange}
              className={styles.checkbox}
            >
              {translate("sale", "end", "date")}
            </Checkbox>
            <Form.Item
              name="saleEndDate"
              className={styles.formItem}
              rules={[
                {
                  required: enabledFields.saleEndDate,
                  message: "Required",
                },
              ]}
            >
              <DatePicker
                format={dateFormat}
                disabled={!enabledFields.saleEndDate}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          {/* Show On Web */}
          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.showOnWeb}
              onChange={handleShowOnWebCheckboxChange}
              className={styles.checkbox}
            >
              {translate("show", "on", "web")}
            </Checkbox>
            <Form.Item
              name="showOnWeb"
              valuePropName="checked"
              className={styles.formItem}
            >
              <Switch disabled={!enabledFields.showOnWeb} />
            </Form.Item>
          </div>

          <div className={styles.formField}>
            <Checkbox
              checked={enabledFields.nonPurchasable}
              onChange={handleNonPurchasableCheckboxChange}
              className={styles.checkbox}
            >
              {translate("non_purchasable")}
            </Checkbox>

            <Space align='start' className='w-100'>
              <Form.Item
                name="nonPurchasable"
                valuePropName="checked"
                className={styles.formItem}
              >
                <Switch disabled={!enabledFields.nonPurchasable} />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate
              >
                {({ getFieldValue }) => {
                  const nonPurchasable = getFieldValue('nonPurchasable') ?? false
                  if (!nonPurchasable) return null
                  return (
                    <Space>
                      <span>{translate("non_purchasable_suppress_price")}</span>
                      <Form.Item
                        name="nonPurchasablePriceEnabled"
                        valuePropName="checked"
                        className={styles.formItem}
                      >
                        <Switch disabled={!enabledFields.nonPurchasable} />
                      </Form.Item>
                    </Space>
                  )
                }}
              </Form.Item>
            </Space>

          </div>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isFormValid() || massChangeLoading}
              loading={massChangeLoading}
            >
              {massChangeLoading
                ? translate("applying")
                : translate("apply", "changes")}
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </div>
  );
};

export default MassChangeForm;
